import { Clock, Effect, Random, Schema as S, Schedule } from "effect";

import { readJson, request } from "../../api/client";
import type { ApiError } from "../../api/errors";
import { randomUuid } from "../../app/idGenerator";
import type { Api } from "../shared";

/**
 * v1 `utils/historyGenerator.ts` + `Developer.tsx` batch runner: backdated
 * records, entries, and edits over the past 30 days, sent as an admin with
 * `source`/`datetime` overrides (the server honors those only for admins).
 * v1's 350-line local faker is replaced by short word lists; the shape and
 * ordering guarantees (creates before edits, records before entries) are kept.
 */

export const GenerationConfig = S.Struct({
	totalRecords: S.Number,
	editsPerRecord: S.Number,
	entriesPerRecordMin: S.Number,
	entriesPerRecordMax: S.Number,
	editsPerEntryMin: S.Number,
	editsPerEntryMax: S.Number,
});
export type GenerationConfig = typeof GenerationConfig.Type;
export type ConfigKey = keyof GenerationConfig;

export const Phase = S.Literals([
	"create-record",
	"edit-record",
	"create-entry",
	"edit-entry",
]);
export type Phase = typeof Phase.Type;

/** `id` is the record id for record steps and the entry id for entry steps. */
export const Step = S.Struct({
	phase: Phase,
	id: S.String,
	datetime: S.String,
	data: S.Unknown,
});
export type Step = typeof Step.Type;

export const Plan = S.Struct({
	steps: S.Array(Step),
	summary: S.Struct({
		records: S.Number,
		recordEdits: S.Number,
		entries: S.Number,
		entryEdits: S.Number,
	}),
});
export type Plan = typeof Plan.Type;

export const defaultConfig: GenerationConfig = {
	totalRecords: 20,
	editsPerRecord: 3,
	entriesPerRecordMin: 2,
	entriesPerRecordMax: 5,
	editsPerEntryMin: 1,
	editsPerEntryMax: 3,
};

export const BATCH_SIZE = 10;
const BATCH_DELAY_MS = 200;
/** 2s, 4s, 8s. */
const RETRY_DELAY_MS = 2000;
const RETRIES = 3;
const SOURCE = "developer-generator";

const FIRST = ["Ada", "Ben", "Chloe", "Dev", "Esme", "Finn", "Gwen", "Hugo"];
const LAST = ["Okafor", "Lindqvist", "Marsh", "Nakamura", "Osei", "Patel"];
const WORDS =
	"the quick brown fox jumps over a lazy dog while the river runs past the mill and the market opens early".split(
		" ",
	);
const TAGS = ["tag1", "tag2", "tag3", "tag4", "tag5"];
const INITIAL_TAGS = TAGS.slice(0, 3);

type RandomGenerator = typeof Random.Random.Service;

const randomInt = (random: RandomGenerator, min: number, max: number): number =>
	Math.floor(random.nextDoubleUnsafe() * (max - min + 1)) + min;
const pick = <T>(random: RandomGenerator, items: ReadonlyArray<T>): T =>
	items[randomInt(random, 0, items.length - 1)] as T;
const randomSubset = <T>(
	random: RandomGenerator,
	items: ReadonlyArray<T>,
	min: number,
	max: number,
): ReadonlyArray<T> => {
	const shuffled = [...items];
	for (let index = shuffled.length - 1; index > 0; index--) {
		const selected = randomInt(random, 0, index);
		[shuffled[index], shuffled[selected]] = [
			shuffled[selected] as T,
			shuffled[index] as T,
		];
	}
	return shuffled.slice(0, randomInt(random, min, max));
};
const sentence = (random: RandomGenerator): string =>
	`${Array.from({ length: randomInt(random, 5, 12) }, () =>
		pick(random, WORDS),
	).join(" ")}.`;
const paragraph = (random: RandomGenerator): string =>
	Array.from({ length: randomInt(random, 2, 4) }, () => sentence(random)).join(
		" ",
	);
const fullName = (random: RandomGenerator): string =>
	`${pick(random, FIRST)} ${pick(random, LAST)}`;
const randomTime = (
	random: RandomGenerator,
	startMs: number,
	endMs: number,
): number => startMs + random.nextDoubleUnsafe() * (endMs - startMs);
const iso = (milliseconds: number): string =>
	new Date(milliseconds).toISOString();

type RecordPlan = Readonly<{
	readonly recordSteps: ReadonlyArray<Step>;
	readonly entrySteps: ReadonlyArray<Step>;
	readonly entries: number;
	readonly entryEdits: number;
}>;

const buildRecordPlan = (
	config: GenerationConfig,
	start: number,
	now: number,
	timestamp: string,
	random: RandomGenerator,
): Effect.Effect<RecordPlan> =>
	Effect.gen(function* () {
		const nodeId = yield* randomUuid;
		const type = pick(random, ["person", "group", "entity", "effort"]);
		const createdMs = randomTime(random, start, now);
		const created = iso(createdMs);
		// The plan's id is sent so the edit steps target the record that was created;
		// PATCH /records/:id validates a full record, so edits carry every field.
		const record = {
			id: nodeId,
			name: fullName(random),
			type,
			description: paragraph(random),
			tags: randomSubset(random, INITIAL_TAGS, 1, 3),
			avatars: [],
			attributes: {},
			timestamp,
		};
		const edits = Array.from({ length: config.editsPerRecord }, () => ({
			phase: "edit-record" as const,
			id: nodeId,
			datetime: iso(randomTime(random, createdMs, now)),
			data: {
				...record,
				name: fullName(random),
				description: paragraph(random),
				tags: randomSubset(random, TAGS, 1, 3),
			},
		}));
		const createRecord: Step = {
			phase: "create-record",
			id: nodeId,
			datetime: created,
			data: record,
		};
		const recordSteps: ReadonlyArray<Step> = [createRecord, ...edits].sort(
			(a, b) => a.datetime.localeCompare(b.datetime),
		);

		const knotCount = randomInt(
			random,
			config.entriesPerRecordMin,
			config.entriesPerRecordMax,
		);
		const entryGroups = yield* Effect.replicateEffect(knotCount)(
			Effect.gen(function* () {
				const knotId = yield* randomUuid;
				const entryCreatedMs = randomTime(random, createdMs, now);
				const entryCreated = iso(entryCreatedMs);
				const create: Step = {
					phase: "create-entry",
					id: knotId,
					datetime: entryCreated,
					data: {
						id: knotId,
						nodeIds: [nodeId],
						type: "note",
						title: sentence(random),
						description: paragraph(random),
						tags: randomSubset(random, INITIAL_TAGS, 0, 3),
						json: "{}",
						attributes: {},
						timestamp,
					},
				};
				const editCount = randomInt(
					random,
					config.editsPerEntryMin,
					config.editsPerEntryMax,
				);
				const entryEdits = Array.from({ length: editCount }, () => ({
					phase: "edit-entry" as const,
					id: knotId,
					datetime: iso(randomTime(random, entryCreatedMs, now)),
					data: {
						title: sentence(random),
						description: paragraph(random),
						tags: randomSubset(random, TAGS, 0, 3),
					},
				}));
				return [create, ...entryEdits].sort((a, b) =>
					a.datetime.localeCompare(b.datetime),
				);
			}),
		);
		return {
			recordSteps,
			entrySteps: entryGroups.flat(),
			entries: knotCount,
			entryEdits: entryGroups.reduce(
				(total, group) => total + group.length - 1,
				0,
			),
		};
	});

export const buildPlan = (config: GenerationConfig): Effect.Effect<Plan> =>
	Effect.gen(function* () {
		const now = yield* Clock.currentTimeMillis;
		const start = now - 30 * 24 * 60 * 60 * 1000;
		const timestamp = iso(now);
		const random = yield* Random.Random;
		const plans = yield* Effect.replicateEffect(config.totalRecords)(
			buildRecordPlan(config, start, now, timestamp, random),
		);
		const recordSteps = plans.flatMap((plan) => plan.recordSteps);
		const entrySteps = plans.flatMap((plan) => plan.entrySteps);
		return {
			steps: [...recordSteps, ...entrySteps],
			summary: {
				records: config.totalRecords,
				recordEdits: config.totalRecords * config.editsPerRecord,
				entries: plans.reduce((total, plan) => total + plan.entries, 0),
				entryEdits: plans.reduce((total, plan) => total + plan.entryEdits, 0),
			},
		};
	});

export const phaseLabel = (phase: Phase): string =>
	({
		"create-record": "Creating records",
		"edit-record": "Editing records",
		"create-entry": "Creating entries",
		"edit-entry": "Editing entries",
	})[phase];

const executeStep = (step: Step): Effect.Effect<unknown, ApiError, Api> => {
	const body = {
		...(step.data as Record<string, unknown>),
		source: SOURCE,
		datetime: step.datetime,
	};
	const read = readJson(S.Unknown);
	switch (step.phase) {
		case "create-record":
			return request(read, { method: "POST", path: "/records", body });
		case "edit-record":
			return request(read, {
				method: "PATCH",
				path: `/records/${step.id}`,
				body,
			});
		case "create-entry":
			return request(read, { method: "POST", path: "/entries", body });
		case "edit-entry":
			return request(read, {
				method: "PATCH",
				path: `/entries/${step.id}`,
				body,
			});
	}
};

const isRateLimited = (error: ApiError): boolean =>
	error._tag === "LimitExceeded" ||
	(error._tag === "Http" && error.status === 429);

/**
 * One batch: every step in parallel; a 429 retries that step alone up to
 * three times with escalating delays (a batch-wide retry would re-create
 * the steps that had already succeeded); any other error is logged and the
 * generation continues (v1). Waits `BATCH_DELAY_MS` before the next batch.
 */
export const runBatch = (
	steps: ReadonlyArray<Step>,
	last: boolean,
): Effect.Effect<void, never, Api> =>
	Effect.all(
		steps.map((step) =>
			executeStep(step).pipe(
				Effect.retry({
					while: isRateLimited,
					schedule: Schedule.exponential(RETRY_DELAY_MS),
					times: RETRIES,
				}),
				Effect.catch((error) => Effect.logError("Batch error", error)),
			),
		),
		{ concurrency: "unbounded", discard: true },
	).pipe(
		Effect.flatMap(() => (last ? Effect.void : Effect.sleep(BATCH_DELAY_MS))),
	);
